import { useState, useCallback } from "react";
/**
 * Shared hook for tooth surface selection state.
 *
 * Supports both controlled (parent owns state via props) and
 * uncontrolled (hook owns state internally) modes.
 *
 * Uses functional setState for stable callbacks (zero deps on state in uncontrolled mode).
 * In controlled mode, callback stability depends on the consumer stabilizing `onSurfaceToggle`.
 */
export function useToothSurfaceSelection(options) {
  const [internalSelected, setInternalSelected] = useState({});
  const [hoveredSurface, setHoveredSurface] = useState(null);
  const selected = options?.selectedSurfaces ?? internalSelected;
  const toggleSurface = useCallback((toothNumber, surface) => {
    if (options?.onSurfaceToggle) {
      options.onSurfaceToggle(toothNumber, surface);
      return;
    }
    setInternalSelected(prev => {
      const current = prev[toothNumber] ?? [];
      const has = current.includes(surface);
      const next = has ? current.filter(s => s !== surface) : [...current, surface];

      // Standard dental charting: surfaces are scoped to one tooth at a time.
      // When selecting a surface on a different tooth, clear all other teeth.
      const hasOtherTeeth = Object.keys(prev).some(k => Number(k) !== toothNumber && (prev[Number(k)]?.length ?? 0) > 0);
      let result;
      if (hasOtherTeeth) {
        // Clear all other teeth, keep only the current tooth
        result = next.length > 0 ? {
          [toothNumber]: next
        } : {};
      } else {
        if (next.length === 0) {
          const {
            [toothNumber]: _,
            ...rest
          } = prev;
          result = rest;
        } else {
          result = {
            ...prev,
            [toothNumber]: next
          };
        }
      }
      return result;
    });
  }, [options?.onSurfaceToggle]);
  const hoverSurface = useCallback((toothNumber, surface) => {
    setHoveredSurface(prev => {
      if (!surface && !prev) return prev;
      if (surface && prev?.toothNumber === toothNumber && prev?.surface === surface) return prev;
      return surface ? {
        toothNumber,
        surface
      } : null;
    });
  }, []);
  return {
    selectedSurfaces: selected,
    hoveredSurface,
    toggleSurface,
    hoverSurface
  };
}